import { useState, useEffect, useRef } from 'react'
import { Bell } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useRecentNotifications } from '@/hooks/useNotifications'
import { NotificationPopover } from './NotificationPopover'

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const [animating, setAnimating] = useState(false)
  const { unreadCount, setUnreadCount } = useNotificationStore()
  const prevCountRef = useRef(unreadCount)
  const { data, dataUpdatedAt } = useRecentNotifications()

  // Sync unread count from server on every successful fetch.
  // dataUpdatedAt ensures the effect runs even when unread_count stays the same
  // (e.g. SSE incremented the store but the server still returns 0).
  useEffect(() => {
    if (data?.unread_count !== undefined) {
      setUnreadCount(data.unread_count)
    }
  }, [data?.unread_count, dataUpdatedAt, setUnreadCount])

  // Animate bell when count increases
  useEffect(() => {
    if (unreadCount > prevCountRef.current) {
      setAnimating(true)
      const timer = setTimeout(() => setAnimating(false), 1000)
      return () => clearTimeout(timer)
    }
    prevCountRef.current = unreadCount
  }, [unreadCount])

  const displayCount = unreadCount > 99 ? '99+' : unreadCount

  return (
    <NotificationPopover open={open} onOpenChange={setOpen}>
      <button
        type="button"
        className="relative cursor-pointer rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        <Bell className={cn('h-5 w-5', animating && 'animate-bounce')} />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
            {displayCount}
          </span>
        )}
      </button>
    </NotificationPopover>
  )
}
